/**
 * The message staff paste to a driver on WhatsApp when they hand them a trip,
 * and the small readings the assign dialog needs around it.
 *
 * Two rules decide what it says:
 *
 * 1. The customer's phone number NEVER appears. The driver portal releases it
 *    only inside 24 hours of pickup (see portalFormat's PHONE_RELEASE_HINT);
 *    a pasted message has no such window — it is forwarded, screenshotted and
 *    kept. Nothing here reads `phone_number` off the booking.
 * 2. A driver who can sign in to the portal gets the short version: time,
 *    pickup, reference, "the details are in your portal". The portal already
 *    holds everything, and a message that gets forwarded should not be
 *    carrying the customer's name and address around with it.
 */
import { DATE_FORMATS, formatBookingField } from '../../../Utils/timezone';
import { driverEventUrl } from '../../../config/config';
import { firstName } from '../../Portal/portalFormat';
import { driverLoginOf, LOGIN_STATES, loginState } from '../../../Utils/portalLogins';

export const LANGUAGES = { HU: 'hu', EN: 'en' };

/**
 * Which language to write in. There is no "own fleet" flag on an operator
 * (ph-core's Operator interface has none), so the honest signal available in
 * this dialog is the driver's own phone number: a Hungarian number (+36 / 06)
 * means a Hungarian-speaking driver — our own fleet and every Budapest
 * subcontractor. Anything else gets English. Staff can override it in the
 * dialog, because a rule this simple will sometimes be wrong.
 */
export const defaultLanguage = (driver) => {
  const digits = String(driver?.phone_number || '').replace(/[\s\-().]/g, '');
  return /^(\+36|0036|36|06)\d/.test(digits) ? LANGUAGES.HU : LANGUAGES.EN;
};

/** 'Mercedes E-Class · Black · NLR-482', or '' when no car is picked. */
export const carLabel = (car) => [car?.type, car?.color, car?.plate_number]
  .map((part) => String(part || '').trim())
  .filter(Boolean)
  .join(' · ');

/** 'Zoltán Nagy · +36 30 111 2233' — what someone picks a driver by. */
export const driverLabel = (driver) => [driver?.full_name, driver?.phone_number]
  .map((part) => String(part || '').trim())
  .filter(Boolean)
  .join(' · ');

/** Head count: the booking's own total, else adults + children, else 0. */
export const passengerCount = (order) => {
  const p = order?.passengers || {};
  const amount = Number(p.amount);
  if (Number.isFinite(amount) && amount > 0) return amount;
  const people = (Number(p.adults) || 0) + (Number(p.children) || 0);
  return people > 0 ? people : 0;
};

const COPY = {
  [LANGUAGES.HU]: {
    label: 'Magyar',
    greeting: (name) => (name ? `Szia ${name}!` : 'Szia!'),
    // The Hungarian message keeps the ISO-ish date the dashboard shows
    // everywhere: no month name to mistranslate, no ambiguity about the day.
    dateFormat: DATE_FORMATS.FULL,
    trip: 'Fuvar',
    pickup: 'Felvétel',
    dropoff: 'Cél',
    passenger: 'Utas',
    people: (n) => `${n} fő`,
    flight: 'Járat',
    car: 'Autó',
    reference: 'Azonosító',
    link: 'Indulásnál és érkezésnél itt jelezz',
    inPortal: 'A részleteket a portálodban találod.',
  },
  [LANGUAGES.EN]: {
    label: 'English',
    greeting: (name) => (name ? `Hi ${name},` : 'Hi,'),
    dateFormat: DATE_FORMATS.EMAIL,
    trip: 'Trip',
    pickup: 'Pickup',
    dropoff: 'Drop-off',
    passenger: 'Passenger',
    people: (n) => `${n} ${n === 1 ? 'person' : 'people'}`,
    flight: 'Flight',
    car: 'Car',
    reference: 'Reference',
    link: 'Tap "on my way" and "arrived" here',
    inPortal: 'The details are in your portal.',
  },
};

export const languageOptions = [LANGUAGES.HU, LANGUAGES.EN]
  .map((value) => ({ value, label: COPY[value].label }));

const line = (label, value) => (value ? `${label}: ${value}` : '');

/**
 * The WhatsApp-ready text for one driver on one booking.
 *
 * @param {object} args
 * @param {object} args.order        the booking
 * @param {object} args.driver       the chosen driver row
 * @param {object} args.car          the chosen car row
 * @param {string} args.language     LANGUAGES.HU | LANGUAGES.EN
 * @param {boolean} args.hasPortal   true when the driver can sign in already
 * @returns {string}
 */
export const driverMessage = ({
  order = {}, driver = null, car = null, language = LANGUAGES.EN, hasPortal = false,
}) => {
  const words = COPY[language] || COPY[LANGUAGES.EN];
  const when = formatBookingField(order, 'date_datetime', words.dateFormat);
  const from = order.destination_from?.address || '';
  const to = order.destination_to?.address || '';
  const reference = order.booking_reference || '';
  const hello = words.greeting(firstName(driver?.full_name));

  // Short shape: the portal has the rest, and a forwarded message should not
  // carry the customer's name or address with it.
  if (hasPortal) {
    return [
      hello,
      [when, from].filter(Boolean).join(' — '),
      line(words.reference, reference),
      words.inPortal,
    ].filter(Boolean).join('\n');
  }

  const count = passengerCount(order);
  const passenger = [order.full_name, count ? `(${words.people(count)})` : '']
    .filter(Boolean).join(' ');
  const url = driverEventUrl(order.operator_id, reference);

  return [
    hello,
    line(words.trip, when),
    line(words.pickup, from),
    line(words.dropoff, to),
    line(words.passenger, passenger),
    line(words.flight, order.flight_number),
    line(words.car, carLabel(car)),
    line(words.reference, reference),
    url ? `\n${words.link}: ${url}` : '',
  ].filter(Boolean).join('\n');
};

/* --- portal logins ------------------------------------------------------- */

/**
 * What the dialog shows about the selected driver's portal access. One of:
 * 'none' (no login), 'invited' (link handed over, never used), 'active'
 * (signed in), 'revoked' (access taken away).
 */
export const PORTAL_STATES = LOGIN_STATES;

export const PORTAL_STATE_LABELS = {
  [PORTAL_STATES.NONE]: { text: 'No portal login', variant: 'secondary', icon: 'fa-user-slash' },
  [PORTAL_STATES.INVITED]: { text: 'Invited — link not used yet', variant: 'warning', icon: 'fa-hourglass-half' },
  [PORTAL_STATES.ACTIVE]: { text: 'Portal login active', variant: 'success', icon: 'fa-circle-check' },
  [PORTAL_STATES.REVOKED]: { text: 'Portal access revoked', variant: 'danger', icon: 'fa-ban' },
};

/**
 * The selected driver's login out of the operator's whole list, and its
 * state — by the operator portal's own rule (Utils/portalLogins.js): the
 * active login, else the newest. A driver re-granted after a revoke has
 * both rows; the first one found is not necessarily theirs.
 */
export const portalStateFor = (users, driverId) => {
  const user = driverLoginOf(users, driverId);
  return { state: loginState(user), user };
};

/** A driver can reach the portal once they have a live login. */
export const canUsePortal = (state) => state === PORTAL_STATES.ACTIVE
  || state === PORTAL_STATES.INVITED;

/* --- what Save will do ---------------------------------------------------- */
/*
 * The API cannot be imported here, so each rule below names the ph-core
 * function it mirrors (paths under src/packages/ph-core/src/), and
 * DriverAssignModal.test / driverHandover.test pin one test per rule. The
 * comment and the tests ARE the contract: change the API, change both.
 */

/**
 * The addresses Save mails, as the API reads the box: split on COMMAS only,
 * each trimmed. Mirrors Helpers/multipleEmailsPrepare.helper.ts
 * `prepareEmailRecipients`; DataValidators/Validators/multipleEmailsValidator.ts
 * refuses anything else as "Invalid email format".
 */
export const emailRecipients = (notifyEmail) => {
  const value = String(notifyEmail || '').trim();
  return value ? value.split(',').map((address) => address.trim()) : [];
};

/**
 * What is wrong with how the box separates addresses, or ''. A semicolon is
 * the usual slip (Outlook's separator), and the API would refuse the whole
 * save over it — so say so rather than quietly reading it as a comma.
 */
export const emailListProblem = (notifyEmail) => (String(notifyEmail || '').includes(';')
  ? 'Separate addresses with commas.'
  : '');

/**
 * The queue Save pushes the driver and car to, or ''. Mirrors
 * Services/PartnerIntegrations/DriverEventsIntegrationService.ts
 * `partnerDriverAssignmentQueue`: the ordered (FIFO) queue once the partner
 * has one, the standard queue otherwise, each trimmed.
 */
export const partnerAssignmentQueue = (partner) => String(partner?.driver_events_queue_fifo || '').trim()
  || String(partner?.driver_events_queue || '').trim();

/**
 * The sentences under the Save button, in the order they happen. Mirrors
 * Controllers/BookingsController.ts `assignDriverOrCar`, condition for
 * condition:
 *
 *  - Email when the box has anything in it — to those addresses and the
 *    office, but to the OFFICE ONLY on an event booking (`isEventBooking`).
 *  - WhatsApp to the customer when the booking has a phone number, the
 *    WhatsApp flag is on, and it is NOT an event booking.
 *  - The driver's phone reaches the customer only inside one of those
 *    customer messages (the email's "Driver Contact Detail", the WhatsApp's
 *    last parameter), only with the toggle on, and only if the driver has a
 *    number — the API sends '-' otherwise. An event booking messages no
 *    customer, so it shares nothing.
 *  - The partner push whenever `partnerDriverAssignmentQueue` finds a queue.
 *
 * @param {object} order   the booking as the dialog holds it
 * @param {object} options `{ driver }` — the driver Save will assign
 * @returns {{ text: string, icon: string }[]} — empty when Save sends nothing.
 */
export const saveEffects = (order = {}, { driver = null } = {}) => {
  const effects = [];
  const isEvent = order.is_event_booking === true;
  const recipients = emailRecipients(order.notify_email);
  const guestEmail = recipients.length > 0 && !isEvent;
  const whatsapp = Boolean(order.notify_whatsapp && order.phone_number && !isEvent);

  if (recipients.length) {
    effects.push(isEvent
      ? { icon: 'fa-envelope', text: 'Emails the office copy only — an event booking never mails the guest' }
      : { icon: 'fa-envelope', text: `Emails ${recipients.filter(Boolean).join(', ')} (and the office copy)` });
  }

  if (whatsapp) {
    effects.push({ icon: 'fa-whatsapp', text: 'Sends the customer a WhatsApp message' });
  }

  if (guestEmail || whatsapp) {
    const phone = String(driver?.phone_number || '').trim();
    if (!order.send_driver_phone_number) {
      effects.push({ icon: 'fa-phone-slash', text: "Keeps the driver's phone number to ourselves" });
    } else if (phone) {
      effects.push({ icon: 'fa-phone', text: `Shares the driver's phone number (${phone}) with the customer` });
    } else {
      // The customer still gets the message — the API puts a '-' where the
      // driver's number would go — so say what they will actually see.
      effects.push({
        icon: 'fa-phone-slash',
        text: driver
          ? 'The customer\'s message will show "-" instead of a driver phone number — this driver has none on file.'
          : 'The customer\'s message will show "-" instead of a driver phone number — no driver is picked yet.',
      });
    }
  }

  if (partnerAssignmentQueue(order.partner)) {
    effects.push({
      icon: 'fa-plug',
      text: `Pushes the driver and car to ${order.partner?.name || 'the partner'} automatically`,
    });
  }

  return effects;
};

/** 'Assign & notify' once Save sends something, plain 'Assign' when it does not. */
export const saveLabel = (order) => (saveEffects(order).length ? 'Assign & notify' : 'Assign');
